import React, { useState } from 'react';
import { 
  Info, 
  X, 
  UserPlus, 
  Activity, 
  Heart, 
  Pill, 
  Utensils, 
  FileText,
  CheckCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';

interface MockDataBannerProps {
  dataType: 'health-conditions' | 'medications' | 'symptoms' | 'food' | 'labs' | 'general';
  onHide?: () => void;
  onStartOnboarding?: () => void;
  onAddData?: () => void;
  showHideOption?: boolean;
  variant?: 'info' | 'warning' | 'success';
}

const dataTypeConfig = {
  'health-conditions': {
    title: 'Sample Health Conditions',
    description: 'These are example conditions to show how the system works.',
    icon: Heart,
    actionText: 'Add Your Conditions',
    benefits: ['Track your specific conditions', 'Get personalized insights', 'Monitor treatment progress']
  },
  'medications': {
    title: 'Sample Medications',
    description: 'These are example medications to demonstrate the tracking features.',
    icon: Pill,
    actionText: 'Add Your Medications',
    benefits: ['Track your prescriptions', 'Check for interactions', 'Set reminders']
  },
  'symptoms': {
    title: 'Sample Symptoms',
    description: 'These are example symptoms to show the tracking capabilities.',
    icon: Activity,
    actionText: 'Log Your Symptoms',
    benefits: ['Track symptom patterns', 'Identify triggers', 'Share with providers']
  },
  'food': {
    title: 'Sample Food Entries',
    description: 'These are example food entries to demonstrate nutrition tracking.',
    icon: Utensils,
    actionText: 'Log Your Meals',
    benefits: ['Track nutrition intake', 'Monitor dietary goals', 'Get meal recommendations']
  },
  'labs': {
    title: 'Sample Lab Results',
    description: 'These are example lab results to show the analysis features.',
    icon: FileText,
    actionText: 'Add Your Lab Results',
    benefits: ['Track health metrics', 'Monitor trends', 'Share with providers']
  },
  'general': {
    title: 'Sample Data',
    description: 'This page contains example data to demonstrate features.',
    icon: Sparkles,
    actionText: 'Add Your Data',
    benefits: ['Personalize your experience', 'Get relevant insights', 'Track your progress']
  }
};

export default function MockDataBanner({
  dataType,
  onHide,
  onStartOnboarding,
  onAddData,
  showHideOption = true,
  variant = 'info'
}: MockDataBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = dataTypeConfig[dataType];
  const IconComponent = config.icon;

  const getVariantStyles = () => {
    switch (variant) {
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const getIconColor = () => {
    switch (variant) {
      case 'warning':
        return 'text-yellow-600';
      case 'success':
        return 'text-green-600';
      default:
        return 'text-blue-600';
    }
  };

  return (
    <div className={`rounded-lg border p-4 mb-6 ${getVariantStyles()}`}>
      <div className="flex items-start space-x-3">
        <IconComponent className={`h-5 w-5 mt-0.5 ${getIconColor()}`} />
        
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">{config.title}</h3>
            <div className="flex items-center space-x-2">
              {showHideOption && onHide && (
                <button
                  onClick={onHide}
                  className="text-xs opacity-70 hover:opacity-100 transition-opacity"
                >
                  Hide
                </button>
              )}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-xs opacity-70 hover:opacity-100 transition-opacity"
              >
                {isExpanded ? 'Less' : 'More'}
              </button>
            </div>
          </div>
          
          <p className="text-sm mt-1 opacity-90">{config.description}</p>
          
          {isExpanded && (
            <div className="mt-3 space-y-3">
              <div className="bg-white/50 rounded-lg p-3">
                <h4 className="text-xs font-semibold mb-2">Why add your real data?</h4>
                <ul className="space-y-1">
                  {config.benefits.map((benefit, index) => (
                    <li key={index} className="text-xs flex items-center space-x-2">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2">
                {onStartOnboarding && (
                  <button
                    onClick={onStartOnboarding}
                    className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-50 text-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    <UserPlus className="h-4 w-4" />
                    <span>Start Onboarding</span>
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}
                
                {onAddData && (
                  <button
                    onClick={onAddData}
                    className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    <IconComponent className="h-4 w-4" />
                    <span>{config.actionText}</span>
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 